const normalizeEmail = (email: string | null | undefined): string | null => {
  if (!email) return null;
  const normalized = email.trim().toLowerCase();
  return normalized || null;
};

const TESTING_ACCOUNT_EMAILS = new Set(['altrionaethar@proton.me']);
const MARKET_FOUNDATIONS_EMAIL_ALLOWLIST = new Set(['amelwani9@gmail.com', 'altrionaethar@proton.me']);
const OWNER_ONLY_EMAIL_ALLOWLIST = new Set(['amelwani9@gmail.com']);

export const isTestingAccountEmail = (email: string | null | undefined): boolean => {
  const normalized = normalizeEmail(email);
  return !!normalized && TESTING_ACCOUNT_EMAILS.has(normalized);
};

export type PrivateAlphaFeature =
  | 'trade_log'
  | 'market_context'
  | 'fed_watch'
  | 'themes'
  | 'weekend_movers';

const PRIVATE_ALPHA_EMAIL_ALLOWLISTS: Record<PrivateAlphaFeature, Set<string>> = {
  trade_log: new Set(['amelwani9@gmail.com']),
  market_context: new Set(['amelwani9@gmail.com', 'altrionaethar@proton.me']),
  fed_watch: new Set(['amelwani9@gmail.com']),
  themes: new Set([
    'amelwani9@gmail.com',
    'altrionaethar@proton.me',
    'cherylmelvy@gmail.com',
    'vaswanidhiren@gmail.com',
    'dvaswani@quantchecklabs.com',
  ]),
  weekend_movers: new Set(['amelwani9@gmail.com', 'altrionaethar@proton.me']),
};

export const canAccessPrivateAlphaFeature = (
  email: string | null | undefined,
  feature: PrivateAlphaFeature
): boolean => {
  const normalized = normalizeEmail(email);
  if (!normalized) return false;
  if (isTestingAccountEmail(normalized)) return true;
  return PRIVATE_ALPHA_EMAIL_ALLOWLISTS[feature].has(normalized);
};

export const canAccessTradeLogFeature = (email: string | null | undefined): boolean =>
  canAccessPrivateAlphaFeature(email, 'trade_log');

export const canAccessMarketContextFeature = (email: string | null | undefined): boolean =>
  canAccessPrivateAlphaFeature(email, 'market_context');

export const canAccessFedWatchFeature = (email: string | null | undefined): boolean =>
  canAccessPrivateAlphaFeature(email, 'fed_watch');

export const canAccessThemesFeature = (email: string | null | undefined): boolean =>
  canAccessPrivateAlphaFeature(email, 'themes');

export const canAccessThemesStrengthChatFeature = (email: string | null | undefined): boolean => {
  const normalized = normalizeEmail(email);
  return !!normalized && OWNER_ONLY_EMAIL_ALLOWLIST.has(normalized);
};

export const canAccessWeekendMoversFeature = (email: string | null | undefined): boolean =>
  canAccessPrivateAlphaFeature(email, 'weekend_movers');

export const canAccessMarketFoundationsFeature = (email: string | null | undefined): boolean => {
  const normalized = normalizeEmail(email);
  return !!normalized && MARKET_FOUNDATIONS_EMAIL_ALLOWLIST.has(normalized);
};

export const canAccessSerenityThesisTrackerFeature = (email: string | null | undefined): boolean => {
  const normalized = normalizeEmail(email);
  return !!normalized && OWNER_ONLY_EMAIL_ALLOWLIST.has(normalized);
};

export type UsEquitiesSubFeature = 'my_scorecard';

const US_EQUITIES_SUBFEATURE_EMAIL_ALLOWLISTS: Record<UsEquitiesSubFeature, Set<string>> = {
  my_scorecard: OWNER_ONLY_EMAIL_ALLOWLIST,
};

export const canAccessUsEquitiesSubFeature = (
  email: string | null | undefined,
  feature: UsEquitiesSubFeature
): boolean => {
  const normalized = normalizeEmail(email);
  return !!normalized && US_EQUITIES_SUBFEATURE_EMAIL_ALLOWLISTS[feature].has(normalized);
};

export const canAccessUsEquitiesMyScorecardFeature = (email: string | null | undefined): boolean =>
  canAccessUsEquitiesSubFeature(email, 'my_scorecard');
